import { useHistory, useLocation } from 'react-router-dom';
import useClipboard from '../../../hooks/useClipboard';
import LangEnum from '../../../lang/LangEnum';
import useSharedEntitystore from '../../../stores/SharedEntities/sharedEntities.store';
import snackbarStore from '../../../stores/Snackbar.store';
import { useApplicationContext } from '../../context/ApplicationContext/ApplicationContext';
import { getWhatsAppLink } from '../../customer/utils/customer.utils';
import { useAuthContext } from '../../login/AuthContext';
import { ordersRepository } from '../../orders/repositories/ordersRepository/orders.repository';
import { useTranslation } from '../../translation/TranslationContext';
import { EntityType } from '../types/sharedEntities.enum';
import { ShareEmailData } from '../types/sharedEntities.types';

interface IShareLinkByWhatsapp {
  message: string;
  phone?: string;
}

interface ISharedEntitiesHookProps {
  enablePublicSharing?: boolean;
}

export default function useSharedEntities({ enablePublicSharing }: ISharedEntitiesHookProps) {
  const sharedEntity = useSharedEntitystore((state) => state.sharedEntity);
  const clipboard = useClipboard();
  const location = useLocation();
  const history = useHistory();
  const { team } = useApplicationContext();
  const { loggedin, loginContext } = useAuthContext();
  const { translate } = useTranslation();
  const { handleMessage } = snackbarStore;
  const canEditCatalogAccess =
    ['ADMIN_MARCA', 'VENDEDOR'].includes(team?.role || '') && !!enablePublicSharing;
  const canEditOrderAccess = ['ADMIN_MARCA', 'VENDEDOR'].includes(team?.role || '');
  const isOrderEntity = sharedEntity?.entity === EntityType.Order;
  const isCatalogEntity = sharedEntity?.entity === EntityType.Catalog;
  const showRetailPrice = loginContext?.enableRetailPrice;
  const showSellOutPrices = sharedEntity?.config.showSellOutPrices;
  const showSellInPrices = sharedEntity?.config.showSellInPrices;
  const showCatalogTab = isCatalogEntity || sharedEntity?.config.catalogId;
  const showOrderTab = isOrderEntity || sharedEntity?.config.catalogId;
  const showShowroomTab = loginContext?.enablePublicShowroom;

  const disableRetailPrice =
    (isCatalogEntity && !showSellOutPrices && !loggedin) || showRetailPrice;
  const disableWholesalePrice = isCatalogEntity && !showSellInPrices && !loggedin;
  const disableProductDetails =
    !loggedin && isCatalogEntity && !sharedEntity.config.enableProductDetails;
  const shouldDisableAllPrices =
    isCatalogEntity && !loggedin && !showSellInPrices && !showSellOutPrices;

  const isPublicAccess = !!sharedEntity?.id && !loggedin;
  const isCatalogActiveTab = location.pathname.includes('catalog');
  const isOrderActiveTab = location.pathname.includes('order');
  const isShowroomActiveTab = !isCatalogActiveTab && !isOrderActiveTab;

  const handleShareLinkByWhatsapp = ({ message, phone }: IShareLinkByWhatsapp) => {
    const whatsAppURL = getWhatsAppLink({ phone, message: window.encodeURIComponent(message) });
    window.open(whatsAppURL, '_blank');
  };

  const handleShareLinkByEmail = async (data: ShareEmailData) => {
    const repository = ordersRepository('');
    const response = await repository.shareOrder(data);

    if (response.ok) {
      handleMessage({
        type: 'success',
        message: translate(LangEnum.CATALOGS_SHARE_MODAL_SEND_EMAILS_SUCCESS),
      });
      return;
    }
    handleMessage({
      type: 'error',
      message: translate(LangEnum.CATALOGS_SHARE_MODAL_SEND_EMAILS_ERROR),
    });
  };

  const handleCopyLink = (message: string) => {
    clipboard.copy(message);
    handleMessage({ type: 'success', message: translate(LangEnum.SNACKBAR_LINK_COPIED) });
  };

  const handleRedirectToShowroom = () => {
    history.push(`/${sharedEntity?.config.slug}/share/showroom`);
  };

  const handleRedirectToCatalog = () => {
    history.push(`/${sharedEntity?.config.slug}/share/catalog/${sharedEntity?.id}`);
  };
  const handleRedirectToOrder = () => {
    history.push(`/${sharedEntity?.config.slug}/share/order/${sharedEntity?.id}/suggestion`);
  };

  return {
    handleRedirectToShowroom,
    handleRedirectToCatalog,
    handleRedirectToOrder,
    handleShareLinkByWhatsapp,
    handleShareLinkByEmail,
    handleCopyLink,
    canEditCatalogAccess,
    canEditOrderAccess,
    isPublicAccess,
    isOrderActiveTab,
    isCatalogActiveTab,
    isShowroomActiveTab,
    disableRetailPrice,
    disableWholesalePrice,
    disableProductDetails,
    shouldDisableAllPrices,
    showCatalogTab,
    showOrderTab,
    showShowroomTab,
    showRetailPrice,
  };
}
